import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Location } from '@angular/common';

@Injectable()
export class UrlModifierService {
	private _location = inject(Location);
	private _route = inject(ActivatedRoute);
	private _joinKeys = [
		'utype',
		'loc',
		'rank',
		'ctype',
		'mode',
		'type',
		'level',
		'date',
	];
	private _replaceKeys = ['ucaspts'];

	private _queryParams: Params = this._route?.snapshot?.queryParams || {};

	/**
	 * Appends the given params to the current URL
	 * @param key the key to append
	 * @param value the value to append
	 * @param baseUrl the base URL to append to
	 * @example url({ page: '2', s: 'test' });
	 * and updates the URL to /baseUrl?page=2&s=test
	 */
	appendToUrl(
		key: string,
		value: string | number,
		baseUrl: string,
		join: boolean | string = false,
	): void {
		const currentRoute = this._location.path();
		const newRoute = this._joinValue(key, value, baseUrl, !!join);

		if (newRoute === currentRoute) {
			return;
		}

		this._location.go(newRoute);
	}

	private _joinValue(
		key: string,
		value: string | number,
		baseUrl: string,
		join: boolean,
	): string {
		let _value = value;
		if (this._queryParams[key] && (this._joinKeys.includes(key) || join)) {
			const split = this._queryParams[key].split('_');

			if (split.includes(value)) {
				split.splice(split.indexOf(value), 1);
			} else {
				split.push(value);
			}
			_value = `${split.join('_')}`;
		}

		// handles removing UCAS from URL
		else if (this._queryParams[key] && this._replaceKeys.includes(key)) {
			if (this._queryParams[key] === value) {
				_value = '';
			}
		}

		return this._updateValue(key, _value, baseUrl);
	}

	private _updateValue(key: string, value: string | number, baseUrl: string): string {
		this._queryParams = {
			...this._queryParams,
			[key]: value,
		};

		return this._generateUrl(baseUrl);
	}

	private _generateUrl(baseUrl: string) {
		const paramsArray = Object.entries(this._queryParams).map(([key, value]) => {
			if (value) {
				return `${key}=${value}`;
			}
			delete this._queryParams[key];
			return;
		});
		const routeUrl = paramsArray.filter((value) => value).join('&');

		return routeUrl ? `${baseUrl}?${routeUrl}` : baseUrl;
	}
}
